import React, { useMemo } from "react";
import withDefaults from "../../../utils/with-defaults";
import { SnippetTypes } from "../../../utils/prop-types";

interface Props {
  type?: SnippetTypes;
  invert?: boolean;
  className?: string;
}

const defaultProps = {
  type: "default" as SnippetTypes,
  invert: false,
  className: "",
};

type NativeAttrs = Omit<React.HTMLAttributes<any>, keyof Props>;
export type TagProps = Props & typeof defaultProps & NativeAttrs;

export type TagColors = {
  color: string;
  bgColor: string;
  borderColor: string;
};

const getColors = (type: SnippetTypes, invert: boolean) => {
  const colors: {
    [key in SnippetTypes]: Pick<TagColors, "color"> & Partial<TagColors>;
  } = {
    default: {
      color: !invert ? "text-foreground" : "text-background",
      bgColor: !invert ? "bg-background" : "bg-foreground",
      borderColor: "border-foreground",
    },
    success: {
      color: !invert ? "text-primary" : "text-on-primary",
      bgColor: !invert ? "bg-on-primary" : "bg-primary",
      borderColor: "border-primary",
    },
    warning: {
      color: !invert ? "text-yellow-500" : "text-background",
      bgColor: invert ? "bg-yellow-500" : "bg-background",
      borderColor: "border-yellow-500",
    },
    error: {
      color: !invert ? "text-red-500" : "text-background",
      bgColor: invert ? "bg-red-500" : "bg-background",
      borderColor: "border-red-500",
    },
    secondary: {
      color: !invert ? "text-accent" : "text-on-accent",
      bgColor: invert ? "bg-accent" : "bg-on-accent",
      borderColor: "border-accent",
    },
    dark: {
      color: invert ? "text-foreground" : "text-background",
      bgColor: invert ? "bg-background" : "bg-foreground",
      borderColor: "border-foreground",
    },
    lite: {
      color: !invert ? "text-foreground" : "text-gray-200",
      bgColor: invert ? "bg-foreground-soft" : "bg-gray-200",
      borderColor: "border-foreground",
    },
  };
  const hideBorder = invert || type === "lite";

  const cardStyle = {
    ...colors[type],
    bgColor: colors[type].bgColor || "bg-background",
    borderColor: hideBorder ? "border-transparent" : colors[type].borderColor,
  };

  return !invert
    ? cardStyle
    : {
        ...cardStyle,
        color: cardStyle.bgColor,
        bgColor: cardStyle.color,
      };
};

const Tag: React.FC<React.PropsWithChildren<TagProps>> = ({
  type,
  children,
  className,
  invert,
  ...props
}) => {
  const { color, bgColor, borderColor } = useMemo(
    () => getColors(type, invert),
    [type, invert]
  );
  return (
    <span
      className={` inline-block leading-3 text-sm h-7 p-2 box-border rounded-md border ${color} ${bgColor} ${borderColor} ${className}`}
      {...props}
    >
      {children}
    </span>
  );
};

const MemoTag = React.memo(Tag);

export default withDefaults(MemoTag, defaultProps);
